import {
  Check,
  X,
  AlertCircle,
  Shield,
  Users,
  Wallet,
  Building,
  ClipboardCheck,
} from 'lucide-react'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui'

interface PermissionDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  showAllRoles?: boolean
}

export function PermissionDrawer({
  open,
  onOpenChange,
  showAllRoles = false,
}: PermissionDrawerProps) {
  const permissions = [
    {
      category: 'Users Permissions',
      icon: <Users className="h-4 w-4" />,
      items: [
        {
          name: 'Can authorize/unauthorize Traders for swaps',
          user: false,
          groupOwner: true,
          superAdmin: true,
          compliance: false,
        },
        {
          name: 'Create, manage and delete Traders',
          user: false,
          groupOwner: true,
          superAdmin: true,
          compliance: false,
        },
        {
          name: 'Create, manage and delete Compliance Officers',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: false,
        },
        {
          name: 'Create, manage and delete Group Owners',
          user: false,
          groupOwner: true,
          superAdmin: true,
          compliance: false,
        },
        {
          name: 'Create, manage and delete Super Admins',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: false,
        },
      ],
    },
    {
      category: 'Deposit Permissions',
      icon: <Wallet className="h-4 w-4" />,
      items: [
        {
          name: 'Can retrieve deposit wallet',
          user: true,
          groupOwner: true,
          superAdmin: true,
          compliance: true,
        },
        {
          name: 'Can deposit into deposit wallet',
          user: true,
          groupOwner: true,
          superAdmin: false,
          compliance: false,
        },
        {
          name: 'Can approve deposits',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: true,
        },
        {
          name: 'Can flag deposits',
          user: true,
          groupOwner: true,
          superAdmin: true,
          compliance: true,
        },
        {
          name: 'Can unflag deposits',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: true,
        },
      ],
    },
    {
      category: 'Swap Permissions',
      icon: <Shield className="h-4 w-4" />,
      items: [
        {
          name: 'Can see how much is currently in their deposit accounts and ready for swaps',
          user: true,
          groupOwner: true,
          superAdmin: true,
          compliance: true,
        },
        {
          name: 'Can swap funds to whitelisted address',
          user: 'conditional',
          groupOwner: true,
          superAdmin: false,
          compliance: false,
          condition: 'Only if Trader is authorized. If not, can only suggest',
        },
        {
          name: 'Can approve swaps for non-authorized Traders',
          user: false,
          groupOwner: true,
          superAdmin: true,
          compliance: false,
        },
        {
          name: 'Can flag swaps',
          user: true,
          groupOwner: true,
          superAdmin: true,
          compliance: true,
        },
        {
          name: 'Can unflag swaps',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: true,
        },
      ],
    },
    {
      category: 'Transaction Permissions',
      icon: <ClipboardCheck className="h-4 w-4" />,
      items: [
        {
          name: 'Can view and download CSV of past transactions',
          user: true,
          groupOwner: true,
          superAdmin: true,
          compliance: true,
        },
        {
          name: 'Can get receipts for past transactions',
          user: true,
          groupOwner: true,
          superAdmin: true,
          compliance: true,
        },
      ],
    },
    {
      category: 'LP Permissions',
      icon: <Building className="h-4 w-4" />,
      items: [
        {
          name: 'Can view LP used for swaps',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: true,
        },
        {
          name: 'Can view LP attached to MSBs',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: true,
        },
        {
          name: 'Can set LP for Groups',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: false,
        },
      ],
    },
    {
      category: 'Whitelist Addresses Permissions',
      icon: <Shield className="h-4 w-4" />,
      items: [
        {
          name: 'Can ask to whitelist wallets',
          user: false,
          groupOwner: true,
          superAdmin: false,
          compliance: false,
        },
        {
          name: 'Can add and approve whitelist wallets',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: false,
        },
      ],
    },
    {
      category: 'Other',
      icon: <ClipboardCheck className="h-4 w-4" />,
      items: [
        {
          name: 'Can view gas wallets',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: true,
        },
        {
          name: 'Can create, manage and delete Company(ies)',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: false,
        },
        {
          name: 'Can create, manage and delete Group(s)',
          user: false,
          groupOwner: false,
          superAdmin: true,
          compliance: false,
        },
      ],
    },
  ]

  const PermissionBadge = ({ status }: { status: boolean | string }) => {
    if (status === true) {
      return (
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100">
          <Check className="h-3 w-3 text-green-600" />
        </div>
      )
    } else if (status === false) {
      return (
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100">
          <X className="h-3 w-3 text-red-500" />
        </div>
      )
    } else if (status === 'conditional') {
      return (
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-100">
          <AlertCircle className="h-3 w-3 text-orange-600" />
        </div>
      )
    }
  }

  const filteredPermissions = permissions
    .map((category) => {
      if (showAllRoles) {
        return {
          ...category,
          items: category.items.filter(
            (item) => item.user || item.groupOwner || item.superAdmin || item.compliance,
          ),
        }
      } else {
        return { ...category, items: category.items.filter((item) => item.user || item.groupOwner) }
      }
    })
    .filter((category) => category.items.length > 0)

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[80vh] mb-16 md:mb-4">
        <DrawerHeader>
          <DrawerTitle>Permission Settings</DrawerTitle>
          <DrawerDescription>Configure role-based access permissions</DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {filteredPermissions.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-8 last:mb-0">
                {/* Category Header */}
                <div className="flex items-center gap-2 pb-4">
                  <div className="p-1.5 bg-muted rounded-md">{category.icon}</div>
                  <h4 className="text-sm font-semibold">{category.category}</h4>
                </div>

                {/* Permissions Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-[40%]">
                          <span className="text-sm font-medium">Permission</span>
                        </TableHead>
                        <TableHead className="text-center w-[15%]">
                          <div className="flex items-center  gap-2">
                            <Users className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium">Trader</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center w-[15%]">
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium">Group Owner</span>
                          </div>
                        </TableHead>
                        {showAllRoles && (
                          <>
                            <TableHead className="text-center w-[15%]">
                              <div className="flex items-center gap-2">
                                <Shield className="h-4 w-4 text-purple-600" />
                                <span className="text-sm font-medium">Super Admin</span>
                              </div>
                            </TableHead>
                            <TableHead className="text-center w-[15%]">
                              <div className="flex items-center gap-2">
                                <ClipboardCheck className="h-4 w-4 text-orange-600" />
                                <span className="text-sm font-medium">Compliance</span>
                              </div>
                            </TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {category.items.map((permission, itemIndex) => (
                        <TableRow key={itemIndex} className="border-b last:border-b-0">
                          <TableCell className="py-4">
                            <p className="text-sm leading-relaxed">{permission.name}</p>
                            {permission.condition && (
                              <p className="text-xs text-orange-600 mt-1 font-medium">
                                {permission.condition}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <PermissionBadge status={permission.user} />
                          </TableCell>
                          <TableCell className="text-center">
                            <PermissionBadge status={permission.groupOwner} />
                          </TableCell>
                          {showAllRoles && (
                            <>
                              <TableCell className="text-center">
                                <PermissionBadge status={permission.superAdmin} />
                              </TableCell>
                              <TableCell className="text-center">
                                <PermissionBadge status={permission.compliance} />
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DrawerFooter className="border-t">
          {/* Legend */}
          <div className="w-full">
            <h4 className="text-xs font-semibold mb-2 uppercase tracking-wider">Legend</h4>
            <div className="flex gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-green-100">
                  <Check className="h-2.5 w-2.5 text-green-600" />
                </div>
                <span className="text-muted-foreground">Allowed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-red-100">
                  <X className="h-2.5 w-2.5 text-red-500" />
                </div>
                <span className="text-muted-foreground">Denied</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-orange-100">
                  <AlertCircle className="h-2.5 w-2.5 text-orange-600" />
                </div>
                <span className="text-muted-foreground">Conditional</span>
              </div>
            </div>
          </div>
          <DrawerClose asChild>
            <Button variant="outline" className="w-full">
              Close
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
